import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, RefreshCw } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import api from '../../services/api';

const CountriesTab = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentCountry, setCurrentCountry] = useState({ name: '', belfiore_code: '' });
  const [errors, setErrors] = useState({});

  // Pagination & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCountries();
  }, [page, rowsPerPage, debouncedSearch]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page, 
        limit: rowsPerPage,
        search: debouncedSearch
      });
      const response = await api.get(`/countries?${queryParams}`);
      setCountries(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      toast.error('Errore nel caricamento delle nazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (country = null) => {
    if (country) {
      setEditMode(true);
      setCurrentCountry(country);
    } else {
      setEditMode(false);
      setCurrentCountry({ name: '', belfiore_code: '' });
    }
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentCountry({ name: '', belfiore_code: '' });
  };

  const validate = () => {
    let tempErrors = {};
    if (!currentCountry.name) tempErrors.name = 'Il nome è obbligatorio';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (editMode) {
        await api.put(`/countries/${currentCountry.id}`, currentCountry);
      } else {
        await api.post('/countries', currentCountry);
      }

      toast.success(editMode ? 'Nazione aggiornata' : 'Nazione creata');
      fetchCountries();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.msg || error.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (country) => {
    setDeleteTarget(country);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/countries/${deleteTarget.id}`);
      toast.success('Nazione eliminata');
      closeDeleteDialog();
      fetchCountries();
    } catch (error) {
      console.error(error);
      toast.error('Impossibile eliminare: probabilmente ci sono dati collegati');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Cerca nazione..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpen()}
        >
          Nuova Nazione
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Codice Belfiore</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" className="py-8">
                  <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                </TableCell>
              </TableRow>
            ) : (
              countries.map((country) => (
                <TableRow key={country.id}>
                  <TableCell>{country.name}</TableCell>
                  <TableCell>{country.belfiore_code || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica">
                      <IconButton size="small" onClick={() => handleOpen(country)} color="primary">
                        <EditIcon size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton size="small" onClick={() => openDeleteDialog(country)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editMode ? 'Modifica Nazione' : 'Nuova Nazione'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Nazione"
            type="text"
            fullWidth
            value={currentCountry.name}
            onChange={(e) => setCurrentCountry({ ...currentCountry, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            margin="dense"
            label="Codice Belfiore (es. Z335 per Marocco)"
            type="text"
            fullWidth
            value={currentCountry.belfiore_code || ''}
            onChange={(e) => setCurrentCountry({ ...currentCountry, belfiore_code: e.target.value.toUpperCase() })}
            helperText="Opzionale. Usato per il calcolo del codice fiscale."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile e può coinvolgere dati collegati. Stai per eliminare la nazione:
          </DialogContentText>
          <Box sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            Codice Belfiore: {deleteTarget?.belfiore_code || '-'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CountriesTab;
